import $ from 'jquery'
import Pusher from 'pusher-js'

// Share-link and anonymous AI chat viewers have no session /pusher/auth can check. Their page
// carries the conversation's token (ChatBot::ConversationComponent#pusher_token), which authorizes
// its private channels instead.
function chatBotAuthParams() {
  const tokens = Array.from(document.querySelectorAll('[data-chat-bot-pusher-token]'))
    .map((element) => element.dataset.chatBotPusherToken)
    .filter(Boolean)
  return tokens.length ? { chat_bot_tokens: tokens.slice(0, 5).join(',') } : {}
}

export default class SimpleroPusher {
  static initClass() {
    this._object = null
    this.prototype._pusher = null
    this.prototype._channel = []
    this.options = {}
  }

  static bind(channel, event, callback, fetch, token) {
    if (this._object == null) {
      this._object = new SimpleroPusher()
    }
    return this._object.bind(channel, event, callback, fetch, token)
  }

  static fetchEvents(channel, event, callback, token) {
    if (this._object == null) {
      this._object = new SimpleroPusher()
    }
    return this._object.fetchEvents(channel, event, callback, token)
  }

  static unbind(channel, event, callback) {
    if (this._object == null) {
      this._object = new SimpleroPusher()
    }
    return this._object.unbind(channel, event, callback)
  }

  static unsubscribe(channel) {
    return this._object?.unsubscribe(channel)
  }

  static trigger(channel, event, data) {
    if (this._object == null) {
      this._object = new SimpleroPusher()
    }
    return this._object.trigger(channel, event, data)
  }

  static get connection() {
    if (this._object == null) {
      this._object = new SimpleroPusher()
    }
    return this._object.connection
  }

  constructor() {
    if (this._pusher == null) {
      const options = { ...$('body').data('pusher-options') }
      options.channelAuthorization = {
        ...options.channelAuthorization,
        paramsProvider: chatBotAuthParams,
      }
      this._pusher = new Pusher($('body').data('pusher-key'), options)
    }
  }

  channel(channel) {
    return (
      this._channel[channel] ||
      (this._channel[channel] = this._pusher.subscribe(channel))
    )
  }

  bind(channel, event, callback, fetch, token) {
    const subscription = this.channel(channel)
    subscription.bind(event, callback)
    if (channel.startsWith('private-chat-bot-')) {
      this.coachBindings ||= new Map()
      if (!this.coachBindings.has(channel))
        this.coachBindings.set(channel, new Map())
      const events = this.coachBindings.get(channel)
      if (!events.has(event)) events.set(event, new Set())
      events.get(event).add(callback)
      if (event === 'pusher:subscription_succeeded' && subscription.subscribed)
        queueMicrotask(callback)
    }
    // Also fetch any events that we may have just missed
    if (fetch) {
      this.fetchEvents(channel, event, callback, token)
      setTimeout(() => {
        this.fetchEvents(channel, event, callback, token)
      }, 2000)
    }
  }

  unbind(channel, event, callback) {
    this._channel[channel]?.unbind(event, callback)
    const events = this.coachBindings?.get(channel)
    if (!events) return
    events.get(event)?.delete(callback)
    if ([...events.values()].some((callbacks) => callbacks.size)) return
    this._pusher.unsubscribe(channel)
    delete this._channel[channel]
    this.coachBindings.delete(channel)
  }

  // For channels with a single owner: drops the subscription itself, not just one callback.
  unsubscribe(channel) {
    if (!this._channel[channel]) return
    this._pusher.unsubscribe(channel)
    delete this._channel[channel]
  }

  fetchEvents(channel, event, callback, token) {
    // Cache reads require a server-signed token (channel-scoped). Without it
    // the endpoint returns 403 — live Pusher subscriptions still work.
    if (!token) {
      return
    }

    return $.ajax({
      url: `/pusher/${channel}/${event}`,
      type: 'POST',
      data: { token },
      global: false,
      success(events, _status, _xhr) {
        return Array.from(events).map((data) => callback.call(null, data))
      },
    })
  }

  trigger(channel, event, data) {
    this.channel(channel).trigger(event, data)
  }

  get connection() {
    return this._pusher.connection
  }
}
SimpleroPusher.initClass()

// System tests deliver a push through the page's own subscription (see capture_staging_test.rb).
if (typeof window !== 'undefined') window.SimpleroPusher = SimpleroPusher
